/**
 * Small pure pieces of the client's intake page: dates entered as day, month
 * name and year and read back in words, placeholders chosen by what a field
 * is, the welcome screen's section outline and the section each question sits
 * in for the progress line.
 */
import type { OutgoingIntakeFormNode } from "@/api/types/outgoingIntake";
import {
  intakeEstimate,
  intakeSteps
} from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";

/* ---------- Dates ---------- */

export type DateParts = { day: string; month: string; year: string };

export const EMPTY_DATE: DateParts = { day: "", month: "", year: "" };

const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

/** "YYYY-MM-DD" when the three parts make a real date, else null. */
export const isoFromParts = ({
  day,
  month,
  year
}: DateParts): string | null => {
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (!/^\d{1,2}$/.test(day.trim()) || !/^\d{4}$/.test(year.trim())) {
    return null;
  }
  if (!Number.isInteger(m) || m < 1 || m > 12) return null;
  if (y < MIN_YEAR || y > MAX_YEAR || d < 1) return null;
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(
    d
  ).padStart(2, "0")}`;
};

export const partsFromIso = (value: unknown): DateParts => {
  const match =
    typeof value === "string" ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  if (!match) return EMPTY_DATE;
  return {
    year: match[1],
    month: String(Number(match[2])),
    day: String(Number(match[3]))
  };
};

/** Day-first in English too, so "03/04" can't be misread. */
const dateLocale = (language: string) =>
  language === "en" ? "en-GB" : language;

/** "14 March 1988" (or "14 de marzo de 1988"). */
export const dateInWords = (iso: unknown, language: string): string | null => {
  const parts = partsFromIso(iso);
  const valid = isoFromParts(parts);
  if (!valid) return null;
  const [y, m, d] = valid.split("-").map(Number);
  try {
    return new Intl.DateTimeFormat(dateLocale(language), {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC"
    }).format(new Date(Date.UTC(y, m - 1, d)));
  } catch {
    return valid;
  }
};

/** January..December in the page's language. */
export const monthNames = (language: string): string[] => {
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat(dateLocale(language), {
      month: "long",
      timeZone: "UTC"
    });
  } catch {
    format = new Intl.DateTimeFormat("en-GB", {
      month: "long",
      timeZone: "UTC"
    });
  }
  return Array.from({ length: 12 }, (_, i) =>
    format.format(new Date(Date.UTC(2000, i, 1)))
  );
};

/* ---------- Placeholders ---------- */

/** A key under `intake.ph.*`, or null for no placeholder (dates, choices). */
export type PlaceholderKey =
  | "email"
  | "phone"
  | "givenName"
  | "familyName"
  | "middleName"
  | "otherNames"
  | "fullName"
  | "number"
  | "city"
  | "country"
  | "employer"
  | "jobTitle"
  | "school"
  | "website"
  | "longText"
  | "answer";

const has = (text: string, ...words: string[]) =>
  words.some((w) => text.includes(w));

export const placeholderKey = (
  node: Pick<
    OutgoingIntakeFormNode,
    "question_type" | "title" | "prompt" | "model_path" | "node_id"
  >
): PlaceholderKey | null => {
  const type = node.question_type ?? "short_text";
  if (type !== "short_text" && type !== "long_text") return null;
  const text = [node.prompt, node.title, node.model_path, node.node_id]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/[_.]/g, " ");
  if (has(text, "email", "e-mail")) return "email";
  if (has(text, "phone", "mobile", "telephone")) return "phone";
  if (has(text, "middle name")) return "middleName";
  if (has(text, "other name", "aliases", "maiden", "names used")) {
    return "otherNames";
  }
  if (has(text, "first name", "given name")) return "givenName";
  if (has(text, "last name", "family name", "surname")) return "familyName";
  if (
    has(
      text,
      "number",
      "receipt",
      "a-number",
      "i-94",
      "passport no",
      "uscis account",
      "ssn"
    )
  ) {
    return "number";
  }
  if (has(text, "website", "url", "linkedin")) return "website";
  if (has(text, "city", "town")) return "city";
  if (has(text, "country")) return "country";
  if (has(text, "employer", "company", "organization")) return "employer";
  if (has(text, "job title", "position", "occupation")) return "jobTitle";
  if (has(text, "school", "university", "institution", "college")) {
    return "school";
  }
  if (has(text, "full name", "name")) return "fullName";
  return type === "long_text" ? "longText" : "answer";
};

/* ---------- Sections ---------- */

export type SectionOutlineRow = {
  id: string;
  title: string;
  questions: number;
  prefilled: number;
  minutes: number;
};

/**
 * The welcome screen's outline: each top-level section (or group outside one)
 * with how many questions it has, how many the firm already filled in and
 * the minutes left in it.
 */
export const sectionOutline = (
  schema: OutgoingIntakeFormNode[],
  answeredIds: ReadonlySet<string>,
  prefilledIds: ReadonlySet<string>
): SectionOutlineRow[] =>
  (schema ?? [])
    .filter(
      (n) => n.node_type === "section" || n.node_type === "repeatable_group"
    )
    .map((node) => {
      const steps = intakeSteps([node]);
      const estimate = intakeEstimate([node], answeredIds);
      return {
        id: String(node.node_id),
        title: String(node.title ?? "").trim(),
        questions: steps.length,
        prefilled: steps.filter((s) => prefilledIds.has(String(s.node_id)))
          .length,
        minutes: estimate.minutes
      };
    })
    .filter((row) => row.questions > 0 && row.title);

/** The top-level section title each numbered question sits in. */
export const sectionByStep = (
  schema: OutgoingIntakeFormNode[]
): Map<string, string> => {
  const out = new Map<string, string>();
  const tops = (schema ?? []).filter(
    (n) =>
      (n.node_type === "section" || n.node_type === "repeatable_group") &&
      String(n.title ?? "").trim()
  );
  for (const node of tops) {
    const title = String(node.title ?? "").trim();
    for (const step of intakeSteps([node])) {
      out.set(String(step.node_id), title);
    }
  }
  return out;
};

/* ---------- Prefill ---------- */

/** How a value the firm already has reads on the confirm card. */
export const prefillDisplay = (
  node: Pick<OutgoingIntakeFormNode, "question_type" | "options">,
  value: unknown,
  language: string,
  yesNo: { yes: string; no: string }
): string => {
  if (node.question_type === "date") {
    return dateInWords(value, language) ?? String(value ?? "");
  }
  if (node.question_type === "boolean") {
    return value === true ? yesNo.yes : value === false ? yesNo.no : "";
  }
  if (node.question_type === "single_select") {
    const option = (node.options ?? []).find((o) => o.option_id === value);
    return String(option?.label ?? value ?? "");
  }
  return String(value ?? "");
};
